import React, { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import GenericToaster from "./theme/create_toast/GenericToaster";
import ResponsiveDrawer from "./router/ResponsiveDrawer";
import AppRoutes from "./router/AppRoutes";
import AppDataInitializer from "./components/AppDataInitializer";
import Keycloak from "keycloak-js";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { Box, AppBar, Toolbar, IconButton, InputBase } from "@mui/material"; // Added mostly for reference if needed, but we use sx
import Store from "./index";
import { useDispatch, useSelector } from "react-redux";
import KeycloackForm from "./components/KeycloackForm";
import helpers from "./UtilitiesFunctions";
import CustomLogin from "./components/CustomLogin";
import { resolveUserRole } from "./utilities/userRoleUtils";

const drawerWidth = 390;

// Styled components using MUI v6 styled API
const Search = styled('div')(({ theme }) => ({
  color: "white",
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.87),
    color: "black",
  },
  marginRight: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(25),
    width: "50rem",
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
  },
}));

const LoginContainer = styled('div')(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(2),
}));

/*
  App Component Refactored to Functional Component for MUI v6 & React 18 Compatibility
*/
function App() {
  const dispatch = useDispatch();
  const theme = useTheme();

  // Redux Selectors
  const cart = useSelector((state) => state.cart);
  const global_config = useSelector((state) => state.globalState);
  const user_role = useSelector((state) => state.usersState.userRole);
  const reduxToken = useSelector((state) => state.usersState.token);
  const isAuthenticated = useSelector((state) => state.authReducer.isAuthenticated);

  // Local State
  const [keycloak, setKeycloak] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userInfo, setUserInfo] = useState("");
  const [token, setToken] = useState("");
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [showLogin, setShowLogin] = useState(true);
  const [loginCredentials, setLoginCredentials] = useState({});

  // Setup Keycloak instance
  const setupKeycloak = () => {
    const url = new URLSearchParams(document.location.search);
    var instance = url.get("appType") + "-";
    if (instance !== "null-") {
      dispatch({
        type: "GET_BASE_URL",
        payload: {
          appType: instance,
        },
      });
    }

    // Prepare config (using props/selectors)
    // Note: global_config might be updated async, but here we assume it's available or we rely on defaults
    // If it depends on the dispatched action above, there might be a race condition in the original code too.
    const newKeycloak = Keycloak({
      realm: global_config.companyName,
      url: global_config.keyCloakAuth,
      "ssl-required": "external",
      resource: global_config.appType + global_config.instanceId,
      clientId: global_config.appType + global_config.instanceId,
      "public-client": true,
      "verify-token-audience": true,
      "use-resource-role-mappings": true,
      "confidential-port": 0,
    });

    newKeycloak.resourceAccess = newKeycloak.resourceAccess || {};
    newKeycloak.resourceAccess.instance = instance;
    setKeycloak(newKeycloak);
    return newKeycloak;
  };

  // Handle successful login
  const handleLoginSuccess = (loginData) => {
    console.log("Login successful with data:", loginData);
    // Extract token
    const accessToken =
      loginData.responseData?.access_token ||
      loginData.responseData?.token ||
      loginData.token ||
      null;

    // Extract user data
    const user = loginData.responseData?.user || {};
    // Get user role
    const role = resolveUserRole(user, loginData.clientId);

    if (accessToken) {
      console.log("Using token authentication");
      // Dispatch to Redux
      dispatch({
        type: "GET_TOKEN",
        token: accessToken,
      });

      if (role) {
        dispatch({
          type: "SET_USER_ROLE",
          userRole: role,
        });
      }

      // Update local state
      setLoginCredentials(loginData);
      setShowLogin(false);
      setAuthenticated(true);
      setToken(accessToken);
      setUserInfo(user);
      setKeycloak(null); // We won't use keycloak anymore

      console.log("Authentication state updated");
    } else {
      console.log("No token in response, showing error");
      toast.error("Authentication failed. No token received.");
    }
  };

  // Initialization logic
  useEffect(() => {
const initialize = async () => {
  const url = new URLSearchParams(document.location.search);
  var companyName = url.get("companyName");
  var instanceId = url.get("instanceId");
  var appType = url.get("appType") || "";  // ADD THIS LINE

  if (companyName != null && instanceId != null) {
    // Always clear data first
    helpers.clearData();
    await dispatch({
      type: "GET_BASE_URL",
      payload: {
        companyName: companyName,
        instanceId: instanceId,
        appType: appType,  // ADD THIS
      },
    });
  }
  setupKeycloak();
};

    initialize();

    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array -> componentDidMount

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const logout = () => {
    dispatch({ type: "CLEAR_USERS_DATA" });
    helpers.LogOut();
    setShowLogin(true);
    setAuthenticated(false);
    setUserInfo("");
    setToken("");
    setKeycloak(null);
  };

  // Render Logic
  const url = new URLSearchParams(document.location.search);
  const urlCompanyName = url.get("companyName");
  const urlInstanceId = url.get("instanceId");
  const urlAppType = url.get("appType") || "";
  const effectiveCompanyName = urlCompanyName || global_config.companyName;
  const configAppType = global_config.appType || "";

  if (!effectiveCompanyName) {
    return <KeycloackForm keycloak={keycloak} />;
  }

  if (showLogin) {
    return (
      <LoginContainer>
        <CustomLogin
          onLoginSuccess={handleLoginSuccess}
          companyName={effectiveCompanyName}
          appType={global_config.appType}
          instanceId={urlInstanceId || global_config.instanceId}
        />
      </LoginContainer>
    );
  }

  if (!authenticated) {
    return (
      <div className="blobcontainer">
        <div className="shapes"></div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="bottom-right"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: {
            boxShadow: "0 0 10px 0 rgba(0,0,0, 0.4)",
            fontWeight: "bold",
          },
        }}
      />
      <GenericToaster />
      <AppDataInitializer
        authenticated={isAuthenticated}
        token={reduxToken}
        appType={global_config.appType}
      />
      <ResponsiveDrawer
        userInfo={userInfo}
        token={reduxToken}
        cartlength={cart?.length}
        appType={global_config.appType}
        bussinesType={global_config.bussinessType}
        onLogout={logout}
        authenticated={isAuthenticated}
        userRole={user_role}
      >
        <AppRoutes
          authenticated={isAuthenticated}
          userRole={user_role}
          appType={global_config.appType}
        />
      </ResponsiveDrawer>
    </>
  );
}

export default App;
